import { useEffect, useRef, useState } from "react";
import { Loader2, Trash2, UserPlus, ChevronDown } from "lucide-react";
import {
  listEventFacilitators,
  inviteEventFacilitator,
  updateEventFacilitatorRole,
  removeEventFacilitator,
} from "../../lib/api";
import { supabase } from "../../lib/supabase";
import type { CoFacilitatorRole, EventFacilitator } from "../../lib/types";

const ROLE_LABEL: Record<CoFacilitatorRole, string> = {
  co_admin: "Co-Admin",
  co_mod: "Co-Moderator",
};

const ROLE_DESCRIPTION: Record<CoFacilitatorRole, string> = {
  co_admin: "Volle Rechte. Kein Löschen des Events oder Verwalten weiterer Co-Facilitators.",
  co_mod: "Moderiert mit. Pausiert, blendet Antworten aus, startet KI-Läufe.",
};

export default function CoFacilitatorCard({ eventId }: { eventId: string }) {
  const [rows, setRows] = useState<EventFacilitator[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<CoFacilitatorRole>("co_mod");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const instanceRef = useRef<string>(crypto.randomUUID());

  const load = async () => {
    setLoading(true);
    try {
      const data = await listEventFacilitators(eventId);
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    const channel = supabase
      .channel(`event_facilitators_card:${eventId}:${instanceRef.current}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "event_facilitators", filter: `event_id=eq.${eventId}` },
        () => { void load(); },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setError("E-Mail erforderlich.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await inviteEventFacilitator(eventId, trimmed, role);
      if (result.status === "invited") {
        showToast(`${trimmed} wurde eingeladen — eine E-Mail mit Anmeldelink ist unterwegs.`);
      } else {
        showToast(`${trimmed} hat jetzt Zugriff.`);
      }
      setEmail("");
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Einladung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRoleChange = async (row: EventFacilitator, next: CoFacilitatorRole) => {
    if (next === row.role) return;
    try {
      await updateEventFacilitatorRole(row.id, next);
      showToast(`Rolle aktualisiert: ${ROLE_LABEL[next]}`);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Rollenwechsel fehlgeschlagen.");
    }
  };

  const handleRemove = async (row: EventFacilitator) => {
    if (!window.confirm(`${row.email} entfernen?`)) return;
    try {
      await removeEventFacilitator(row.id);
      showToast(`${row.email} entfernt.`);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Entfernen fehlgeschlagen.");
    }
  };

  return (
    <section className="rounded-2xl border border-rc-fog bg-rc-paper p-6 sm:p-7 max-w-xl">
      <header className="mb-5">
        <h3 className="text-rc-black font-bold text-[18px] tracking-[-0.01em]">Co-Facilitators</h3>
        <p className="text-rc-charcoal text-[13px] leading-[1.5] mt-1.5">
          Lade weitere Personen in dieses Event ein. Co-Admins haben dieselben Rechte wie du,
          außer das Event zu löschen oder weitere Co-Facilitators zu verwalten.
          Co-Moderators moderieren mit, dürfen aber keine Fragen oder Einstellungen ändern.
        </p>
      </header>

      {loading ? (
        <div className="py-8 flex items-center justify-center" role="status">
          <Loader2 className="w-4 h-4 text-rc-berry animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-rc-slate text-[13px] italic mb-5">Noch niemand eingeladen.</p>
      ) : (
        <ul className="flex flex-col gap-2 mb-5">
          {rows.map((row) => (
            <li key={row.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-rc-fog">
              <div className="flex-1 min-w-0">
                <div className="text-rc-black text-[14px] font-semibold truncate">{row.email}</div>
                <div className="text-rc-slate text-[12px]">
                  {row.accepted_at ? "Aktiv" : "Eingeladen"}
                </div>
              </div>
              <RolePicker
                value={row.role}
                onChange={(next) => handleRoleChange(row, next)}
              />
              <button
                type="button"
                onClick={() => handleRemove(row)}
                aria-label={`${row.email} entfernen`}
                className="p-2 text-rc-slate hover:text-rc-berry hover:bg-rc-berry/8 rounded-full transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <form onSubmit={handleInvite} className="flex flex-col gap-3 pt-5 border-t border-rc-fog">
        <label className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em]">
          Hinzufügen
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            inputMode="email"
            autoComplete="off"
            placeholder="kollegin@beispiel.de"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2.5 text-[14px] text-rc-black placeholder:text-rc-slate/70 focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
          />
          <RolePicker value={role} onChange={setRole} />
          <button
            type="submit"
            disabled={submitting || !email.trim()}
            className="inline-flex items-center justify-center gap-2 bg-rc-berry text-rc-paper rounded-pill px-5 py-2.5 text-[14px] font-semibold hover:bg-rc-berry-700 disabled:opacity-50 transition active:scale-[0.97]"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Hinzufügen
          </button>
        </div>
        <p className="text-rc-slate text-[12px]">{ROLE_DESCRIPTION[role]}</p>
        {error && <p className="text-rc-berry text-[12px] font-medium">{error}</p>}
      </form>

      {toast && (
        <div
          role="status"
          className="mt-3 text-[12px] text-rc-charcoal bg-rc-cream border border-rc-fog rounded-xl px-3 py-2"
        >
          {toast}
        </div>
      )}
    </section>
  );
}

function RolePicker({
  value,
  onChange,
}: {
  value: CoFacilitatorRole;
  onChange: (next: CoFacilitatorRole) => void;
}) {
  return (
    <div className="relative inline-flex">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as CoFacilitatorRole)}
        className="appearance-none bg-rc-paper border-[1.5px] border-rc-fog rounded-xl pl-3 pr-8 py-2.5 text-[14px] text-rc-black font-semibold focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
      >
        <option value="co_admin">Co-Admin</option>
        <option value="co_mod">Co-Moderator</option>
      </select>
      <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-rc-slate pointer-events-none" />
    </div>
  );
}
